import StreamingHub from '../../services/StreamingHub';
import MessageHub from '../../services/MessageHub';
import GroupChatHub from '../../services/GroupChatHub';
import { Device } from '../../models/Device';
import { IDownloadSessionProgressMessage } from '../../models/DownloadSession';
import { normalizeChatMessage } from '../../services/GroupChatService';
import { registerStreamRecordingUpdateHandler } from '../../utils/StreamRecordingRefresh';
import { registerDownloadCommandsChangedHandler } from '../../utils/DownloadCommandsChangedRefresh';
import { registerPigRunNotificationsChangedHandler } from '../../utils/PigRunNotificationsChangedRefresh';

// Cap the dedup array to prevent unbounded growth during long pig runs.
const MAX_PASSAGE_TIMESTAMPS = 500;

// Guard against duplicate handler registration on reconnect.
let signalRHandlersRegistered = false;

export default {
  state: {},
  mutations: {},
  actions: {
    initSignalR(context: any) {
      MessageHub.init();
      StreamingHub.init();
      GroupChatHub.init();

      const connection = MessageHub.getConnection();

      // Skip re-registration if handlers are already wired up.
      if (signalRHandlersRegistered) {
        return;
      }
      signalRHandlersRegistered = true;

      const recievedPassageTimestamps: string[] = [];

      // ── Heartbeat batching ────────────────────────────────────
      // Buffer incoming heartbeats keyed by UID (latest wins) and
      // flush once per animation frame so dozens of SignalR
      // messages collapse into a single Vuex commit + dispatch.
      const pendingHeartbeats: Map<number, any> = new Map();
      let heartbeatRafId: number | null = null;

      function flushHeartbeats() {
        heartbeatRafId = null;
        if (pendingHeartbeats.size === 0) return;
        const batch = Array.from(pendingHeartbeats.values());
        pendingHeartbeats.clear();
        context.commit('UPDATE_LOCATIONS_BATCH', batch);
        context.dispatch('heartbeatBatchReceived', batch);
      }

      // ── Cached UID set ────────────────────────────────────────
      // Built from the devices array and lazily rebuilt when the
      // array length changes, so every SignalR handler does an
      // O(1) Set.has() instead of allocating a new array.
      let groupUidSet: Set<number> = new Set(
        context.rootState.device.devices.map((d: Device) => d.uid)
      );
      let cachedDevicesLength = context.rootState.device.devices.length;

      const isUidInGroup = (uid: number): boolean => {
        const devices = context.rootState.device.devices;
        if (devices.length !== cachedDevicesLength) {
          groupUidSet = new Set(devices.map((d: Device) => d.uid));
          cachedDevicesLength = devices.length;
        }
        return groupUidSet.has(uid);
      };

      const isRunIdActive = (runId: number) => {
        return runId in (context.rootState.pigRun.pigRuns || {});
      };

      connection.on('ReceiveCellularCarrierInfo', (data: any) => {
        if (isUidInGroup(data.uid)) {
          context.dispatch('setCellularCarrierInfo', data);
        }
      }),
        // ReceiveDiskUse SignalR event handler.
        connection.on('ReceiveDiskUse', (diskUseMsg: any) => {
          context.dispatch('setDiskUse', diskUseMsg);
        }),
        connection.on('ReceivePigRunUpdate', (data: any) => {
          if (isRunIdActive(data.runId)) {
            if (data.messagegName == 'UpdateLocation') {
              context.commit('UPDATE_PIG_LOCATION', {
                pigRunId: data.runId,
                lat: data.lat,
                lon: data.lon,
                speed: data.speed,
                serverTime: data.serverTime,
                etaToNext: data.etAtoNextString,
                etaToNextTrack: data.etAtoNextTrackString,
                etaToEnd: data.etAtoEndString,
              });
              context.dispatch('updatePigLoc', {
                pigRunId: data.runId,
                lat: data.lat,
                lon: data.lon,
              });
            } else if (data.messagegName == 'UpdateLocationV2') {
              context.commit('UPDATE_PIG_LOCATION', {
                pigRunId: data.runId,
                lat: data.lat,
                lon: data.lon,
                speed: data.speed,
                serverTime: data.serverTime,
                etaToNext: data.etAtoNextV2,
                etaToNextTrack: data.etAtoNextTrackV2,
                etaToEnd: data.etAtoEndV2,
              });
              context.dispatch('updatePigLocV2', {
                pigRunId: data.runId,
                lat: data.lat,
                lon: data.lon,
              });
            } else {
              context.dispatch('refreshSinglePigRun', {
                pigRunId: data.runId,
                force: true,
              });
            }
          } else {
            const completedPigRunId = Number(data.runId);
            if (
              Number.isInteger(completedPigRunId) &&
              completedPigRunId > 0 &&
              context.rootState.pigRun?.selectedCompletedPigRunId ===
                completedPigRunId
            ) {
              context.commit(
                'REQUEST_COMPLETED_TRACKING_WORKSHEET_REFRESH',
                completedPigRunId
              );
            }

            // Run not currently active — re-fetch active runs
            context.dispatch('getActivePigRuns');
          }
        }),
        connection.on('ReceiveDownloadMode', (data: any) => {
          // IMPORTANT - this message was named incorectly, its really the record mode that it is updating
          if (isUidInGroup(data.uid)) {
            context.dispatch('updateStatus', {
              uid: data.uid,
              isConnected: true,
            });
            context.dispatch('setRecordMode', data);
          }
        }),
        connection.on('ReceiveCommTypeChange', (data: any) => {
          if (isUidInGroup(data.uid)) {
            context.dispatch('setIsIridium', data);
          }
        }),
        connection.on('ReceiveHeartbeat', (data: any) => {
          if (isUidInGroup(data.uid)) {
            if (data.lat !== 0 && data.lon !== 0) {
              pendingHeartbeats.set(data.uid, data);
              if (heartbeatRafId === null) {
                heartbeatRafId = requestAnimationFrame(flushHeartbeats);
              }
            }
            if (context.rootState.notifications.heartbeat) {
              context.dispatch(
                'notify',
                'Recieved Heartbeat on UID ' + data.uid
              );
            }
          }
        });
      connection.on('ReceiveMlResult', (results: any[]) => {
        if (!Array.isArray(results) || results.length === 0) return;
        const relevantResults = results.filter((r) => isUidInGroup(r.uid));
        if (relevantResults.length === 0) return;

        // Extract distinct UIDs and pigRunIds so components can filter.
        // Support both `pigRunId` (new API) and `runId` (legacy) field names.
        const uids = [...new Set(relevantResults.map((r) => r.uid))];
        const pigRunIds = [
          ...new Set(
            relevantResults
              .map((r) => r.pigRunId ?? r.runId)
              .filter((id) => id != null)
          ),
        ];
        context.dispatch('mlResultReceived', {
          uids,
          pigRunIds,
          results: relevantResults,
        });
      });
      connection.on('ReceiveConnectionUpdate', (data: any) => {
        if (isUidInGroup(data.uid)) {
          context.dispatch('updateStatus', data);
        }
      });
      connection.on('ReceiveDeviceInformationResponse', (data: any) => {
        if (isUidInGroup(data.uid)) {
          context.dispatch('deviceInfoReceived', data);
        }
      });
      connection.on('ReceiveControlSettingsResponse', (data: any) => {
        if (isUidInGroup(data.uid)) {
          context.dispatch('controlSettingsReceived', data);
        }
      });
      connection.on('ReceivePassage', (data: any) => {
        if (isUidInGroup(data.uid)) {
          // Dispatch action = [updateStatus].
          context.dispatch('updateStatus', {
            uid: data.uid,
            isConnected: true,
          });

          context.dispatch('passageRecieved', data);
          context.dispatch('updateDetectionCount', data);

          // ELF/GEO/MAG (packetComplexInformation) are treated as seperate passages so we want to avoid double notifications
          if (
            context.rootState.notifications.passage &&
            recievedPassageTimestamps.indexOf(data.passageTimeStamp) === -1
          ) {
            context.dispatch(
              'notify',
              'Recieved Passage  ' + data.detectionCount + ' on UID ' + data.uid
            );
          }

          if (
            context.rootState.notifications.passageSound &&
            context.rootState.notifications.passageSound !== '' &&
            recievedPassageTimestamps.indexOf(data.passageTimeStamp) === -1
          ) {
            context.dispatch(
              'notifySound',
              context.rootState.notifications.passageSound
            );
          }

          recievedPassageTimestamps.push(data.passageTimeStamp);
          // Evict oldest entries to prevent unbounded growth during long runs.
          if (recievedPassageTimestamps.length > MAX_PASSAGE_TIMESTAMPS) {
            recievedPassageTimestamps.splice(
              0,
              recievedPassageTimestamps.length - MAX_PASSAGE_TIMESTAMPS
            );
          }
        }
      });
      connection.on('ReceiveCommandResponse', (data: any) => {
        if (isUidInGroup(data.uid)) {
          // If we are receiving command responses the device is connected.
          context.dispatch('updateStatus', {
            uid: data.uid,
            isConnected: true,
          });

          if (data.command === 'CommandResponseNotReceived') {
            context.dispatch(
              'notify',
              'Command response not received. Deleting command.'
            );
          }

          if (data.command === 'deviceInfo') {
            if (context.rootState.notifications.deviceInfo) {
              context.dispatch(
                'notify',
                'Received Device Info command on UID ' + data.uid
              );
            }
            if (
              context.rootState.notifications.deviceInfoSound &&
              context.rootState.notifications.deviceInfoSound !== ''
            ) {
              context.dispatch(
                'notifySound',
                context.rootState.notifications.deviceInfoSound
              );
            }
          }
          if (data.command === 'controlSettings') {
            context.dispatch('getDevice', data.uid);
            if (context.rootState.notifications.controlSetting) {
              context.dispatch(
                'notify',
                'Received Control Setting command on UID ' + data.uid
              );
            }
            if (
              context.rootState.notifications.controlSettingSound &&
              context.rootState.notifications.controlSettingSound !== ''
            ) {
              context.dispatch(
                'notifySound',
                context.rootState.notifications.controlSettingSound
              );
            }
          }
          if (data.command === 'gpsFix') {
            if (context.rootState.notifications.gpsFix) {
              context.dispatch(
                'notify',
                'Received GPS Fix command on UID ' + data.uid
              );
            }
            if (
              context.rootState.notifications.gpsFixSound &&
              context.rootState.notifications.gpsFixSound !== ''
            ) {
              context.dispatch(
                'notifySound',
                context.rootState.notifications.gpsFixSound
              );
            }
          }
          if (data.command === 'powerMode') {
            context.dispatch('getDevice', data.uid);
            if (context.rootState.notifications.powerMode) {
              context.dispatch(
                'notify',
                'Received Power Mode command on UID ' + data.uid
              );
            }
            if (
              context.rootState.notifications.powerModeSound &&
              context.rootState.notifications.powerModeSound !== ''
            ) {
              context.dispatch(
                'notifySound',
                context.rootState.notifications.powerModeSound
              );
            }
          }
          if (data.command === 'frequencySelect') {
            context.dispatch('getDevice', data.uid);
            if (context.rootState.notifications.frequencySelect) {
              context.dispatch(
                'notify',
                'Received Frequency Select command on UID ' + data.uid
              );
            }
            if (
              context.rootState.notifications.frequencySelectSound &&
              context.rootState.notifications.frequencySelectSound !== ''
            ) {
              context.dispatch(
                'notifySound',
                context.rootState.notifications.frequencySelectSound
              );
            }
          }
          if (data.command === 'streamEnable') {
            context.dispatch('getDevice', data.uid);
            if (context.rootState.notifications.streamEnable) {
              context.dispatch(
                'notify',
                'Received Stream Enable command on UID ' + data.uid
              );
            }
            if (
              context.rootState.notifications.streamEnableSound &&
              context.rootState.notifications.streamEnableSound !== ''
            ) {
              context.dispatch(
                'notifySound',
                context.rootState.notifications.streamEnableSound
              );
            }
          }
          if (data.command === 'recordMode') {
            context.dispatch('getDevice', data.uid);
            if (context.rootState.notifications.recordMode) {
              context.dispatch(
                'notify',
                'Received Record Mode command on UID ' + data.uid
              );
            }
            if (
              context.rootState.notifications.recordModeSound &&
              context.rootState.notifications.recordModeSound !== ''
            ) {
              context.dispatch(
                'notifySound',
                context.rootState.notifications.recordModeSound
              );
            }
          }
          if (data.command === 'detectCountReset') {
            context.dispatch('getDevice', data.uid);
            if (context.rootState.notifications.detectionCount) {
              context.dispatch(
                'notify',
                'Received Detection Count Reset command on UID ' + data.uid
              );
            }
            if (
              context.rootState.notifications.detectionCountSound &&
              context.rootState.notifications.detectionCountSound !== ''
            ) {
              context.dispatch(
                'notifySound',
                context.rootState.notifications.detectionCountSound
              );
            }
          }
          if (
            context.rootState.device.selectedDevice &&
            context.rootState.device.selectedDevice.uid === data.uid
          ) {
            context.dispatch('getCommands', data.uid);
          }
        }
      });

      // ── Download session progress ─────────────────────────────
      // Dispatched when any user in the active group initiates a
      // passage download session. The action commits the mutation
      // and the panel subscribes via subscribeAction for debounced
      // passage fetching.
      connection.on(
        'ReceiveDownloadSessionProgress',
        (data: IDownloadSessionProgressMessage) => {
          if (isUidInGroup(data.uid)) {
            context.dispatch('downloadSessionStateReceived', data);
          }
        }
      );

      // ── Stream session updates ───────────────────────────────
      // Broadcast by the backend when a user joins or leaves a
      // stream session. Re-fetches the full session list so the
      // Stream Manager panel stays current.
      connection.on('ReceiveStreamSessionUpdate', (data: any) => {
        if (isUidInGroup(data.uid)) {
          context.dispatch('fetchActiveStreamSessions');
          context.dispatch('streamSessionUpdateReceived', data);
        }
      });

      // ── Stream recording auto-stopped ─────────────────────────
      // Broadcast by the backend when a recording is automatically
      // stopped (e.g. max duration exceeded). The RealTimeStream
      // component listens for this to clear the recording indicator.
      connection.on('ReceiveStreamRecordingStopped', (data: any) => {
        if (isUidInGroup(data.uid)) {
          context.dispatch('streamRecordingAutoStopped', data);
        }
      });

      // ── Stream recording started / stopped (EN-1261) ──────────
      // Broadcast by the backend when anyone in the group starts a
      // recording or stops one manually. The Stream Manager's recordings
      // list refetches quietly, and an open plot of that device follows
      // it (EN-1552 stop, EN-1556 start).
      registerStreamRecordingUpdateHandler(
        connection,
        isUidInGroup,
        (type, payload) => context.dispatch(type, payload)
      );

      // ── Queued downloads changed (EN-1557) ────────────────────
      // Sent to the device's own group when anyone
      // queues, cancels, edits, retries or deletes a queued download, or
      // when the Download Manager sends it. The Download Sessions cards
      // reload from the WebApi (debounced), even with the dialog closed.
      registerDownloadCommandsChangedHandler(
        connection,
        isUidInGroup,
        (type, payload) => context.dispatch(type, payload)
      );

      // ── Pig run user notifications changed (EN-1569) ──────────
      // Sent to the run's own group when anyone saves the run's user
      // notification list. The viewer lists and any open Notifications
      // dialog of that run re-read (debounced), and other users get a
      // short toast.
      registerPigRunNotificationsChangedHandler(connection, (type, payload) =>
        context.dispatch(type, payload)
      );

      // ── Group chat ────────────────────────────────────────────
      // Chat uses a dedicated SignalR hub (/chathub) so the
      // handlers are wired to GroupChatHub, not the AgmHub
      // connection. Messages are group-scoped on the server so
      // no client-side UID/group filtering is needed.
      const chatConnection = GroupChatHub.getConnection();

      chatConnection.on('ReceiveChatMessage', (data: any) => {
        context.dispatch(
          'chatMessageReceived',
          normalizeChatMessage({
            id: data.groupChatMessageId,
            groupChatConversationId: data.groupChatConversationId,
            messageType: data.messageType,
            userId: data.userId,
            userName: data.userName,
            text: data.messageText,
            timestamp: data.createdDateUtc,
            attachment: data.attachment ?? null,
            citationJson: data.citationJson,
            proposedActionJson: data.proposedActionJson,
            aiQuerySourceMessageId: data.aiQuerySourceMessageId,
          })
        );
      });

      chatConnection.on('ReceiveChatConversationCreated', (data: any) => {
        context.dispatch('chatConversationCreated', data ?? {});
      });

      chatConnection.on('ReceiveChatVisibilityChanged', () => {
        window.dispatchEvent(new Event('chat-visibility-changed'));
      });

      chatConnection.on('ReceiveChatMessageDeleted', (data: any) => {
        context.dispatch(
          'chatMessageDeleted',
          typeof data === 'number'
            ? { messageId: data, groupChatConversationId: null }
            : data
        );
      });

      chatConnection.on('ReceiveTypingIndicator', (data: any) => {
        context.dispatch('chatTypingReceived', data ?? {});
      });

      chatConnection.on('ReceiveReactionUpdate', (data: any) => {
        context.dispatch('reactionReceived', {
          messageId: data.messageId,
          groupChatConversationId: data.groupChatConversationId,
          userId: data.userId,
          emoji: data.emoji,
          added: data.added,
        });
      });
    },
  },
};
